import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import MovieIcon from '@mui/icons-material/Movie';
import CampaignIcon from '@mui/icons-material/Campaign';
import WarningIcon from '@mui/icons-material/Warning';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AttachmentOutlinedIcon from '@mui/icons-material/AttachmentOutlined';

import { getLinkLtik } from 'constants/LTIData';

import classes from './CourseIncludes.module.css';

/**
 * CourseIncludes Component
 *
 * A comprehensive React component that displays the main features and included resources of a course.
 * Handles dynamic rendering of course progress, LTI integration messages, and lists of included materials.
 *
 * Features:
 * - Progress calculation and display for course activities
 * - Conditional rendering of LTI integration messages and warnings
 * - Responsive UI for both access and no-access states
 * - Multi-language support via i18n
 * - Iconography for visual clarity
 * - PropTypes and defaultProps for robust type checking
 *
 * @param {Object} props - Component props
 * @param {Object} props.dataCourse - Complete course data including lessons, activities, and user progress
 * @param {string} props.lessons - Number of lessons included in the course
 * @param {string} props.aditionalMaterial - Number of additional materials/files included
 * @param {string} props.activities - Number of activities included in the course
 * @param {string} props.hrs - Total hours of course content
 * @param {Object} props.ltiData - LTI integration data for grade passback and external platform communication
 * @param {boolean} props.accessCourse - Whether the user has access to the course
 * @returns {JSX.Element} The rendered course includes section
 */

const CourseIncludes = props => {
  const dataUser = useSelector(state => state.userInfo);
  const dataAcademy = useSelector(state => state.academyInfo);

  let allActivities = 0;
  let completeActivities = 0;
  props.dataCourse.userAditionalTrack.userTrack.map(progress => {
    if (progress.media_play == 2) {
      completeActivities += 1;
    }
    if (progress.media_eval == 2) {
      completeActivities += 1;
    }
    if (progress.media_coeval >= 3) {
      completeActivities += 1;
    }
    if (progress.media_quiz == 2) {
      completeActivities += 1;
    }
  });
  if (props.dataCourse.lessonsCourse.separators) {
    props.dataCourse.lessonsCourse.separators.map(folder => {
      folder.lessons.map(video => {
        allActivities += 1;
        if (video.hasEvaluation) {
          allActivities += 2;
        }
        if (video.idQuiz > 0) {
          allActivities += 1;
        }
      });
    });
  }
  if (props.dataCourse.lessonsCourse.lessons) {
    props.dataCourse.lessonsCourse.lessons.map(video => {
      allActivities += 1;
      if (video.hasEvaluation) {
        allActivities += 2;
      }
      if (video.idQuiz > 0) {
        allActivities += 1;
      }
    });
  }

  // TODO: Eliminar estas lineas cuando el back mande bien la info
  if (completeActivities > allActivities) {
    completeActivities = allActivities;
  }

  const progress = (completeActivities * 100) / allActivities;
  const { t } = useTranslation(['course']);
  return (
    <>
      <div
        className={
          props.accessCourse
            ? classes.productLearnInfoAccessSection
            : classes.productLearnInfoSection
        }
      >
        {props.ltiData && (
          <>
            {props.ltiData.gradeRes && (
              <>
                {dataAcademy.data.integration === 'UVM_LIC_BB' &&
                  (getLinkLtik() ? (
                    <>
                      {props.ltiData.gradeReq.tool.startsWith(
                        'mn'
                      ) &&
                        progress === 100 && (
                          <div className={classes.warningMessage}>
                            <p className={classes.title}>
                              {dataUser.data.nombre}, terminaste tu
                              bloque!
                            </p>
                            <div className={classes.infoContainer}>
                              <WarningIcon
                                className={classes.icon}
                              />
                              <p className={classes.messateText}>
                                Como siguiente paso, obtén tu
                                calificación. Para hacerlo, ingresa
                                al enlace en el menú de Blackboard,
                                ubicado en tu materia, con el nombre
                                “Módulo de acreditación Mayahii”.
                              </p>
                            </div>
                          </div>
                        )}
                      {props.ltiData.gradeRes.response.isConfirm &&
                        props.ltiData.gradeRes.response.isReady && (
                          <div className={classes.successMessage}>
                            <p className={classes.title}>
                              {dataUser.data.nombre} ¡felicidades!
                            </p>
                            <div className={classes.infoContainer}>
                              <CampaignIcon
                                className={classes.icon}
                              />
                              <p className={classes.messateText}>
                                Tu calificación se ha enviado a
                                Blackboard y registrado con éxito.
                                Gracias por tu compromiso.
                              </p>
                            </div>
                          </div>
                        )}
                      {!props.ltiData.gradeRes.response.isConfirm &&
                        props.ltiData.gradeRes.response.isReady && (
                          <div className={classes.warningMessage}>
                            <p className={classes.title}>
                              {dataUser.data.nombre}, terminaste tu
                              bloque!
                            </p>
                            <div className={classes.infoContainer}>
                              <WarningIcon
                                className={classes.icon}
                              />
                              <p className={classes.messateText}>
                                Como siguiente paso, obtén tu
                                calificación. Para hacerlo, ingresa
                                al enlace en el menú de Blackboard,
                                ubicado en tu materia, con el nombre
                                “Módulo de acreditación Mayahii”.
                              </p>
                            </div>
                          </div>
                        )}
                      {!props.ltiData.gradeRes.response &&
                        progress === 100 && (
                          <div className={classes.warningMessage}>
                            <p className={classes.title}>
                              {dataUser.data.nombre}, terminaste tu
                              bloque!
                            </p>
                            <div className={classes.infoContainer}>
                              <WarningIcon
                                className={classes.icon}
                              />
                              <p className={classes.messateText}>
                                Como siguiente paso, obtén tu
                                calificación. Para hacerlo, ingresa
                                al enlace en el menú de Blackboard,
                                ubicado en tu materia, con el nombre
                                “Módulo de acreditación Mayahii”.
                              </p>
                            </div>
                          </div>
                        )}
                    </>
                  ) : (
                    <div className={classes.errorMessage}>
                      <p className={classes.errorTitle}>
                        {dataUser.data.nombre}
                      </p>
                      <div className={classes.infoContainer}>
                        <WarningIcon className={classes.icon} />
                        <p className={classes.messateText}>
                          La calificación{' '}
                          <strong>
                            no será enviada a Blackboard
                          </strong>{' '}
                          ya que no has accedido a través del enlace
                          correspondiente a tu semana de evaluación.
                        </p>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </>
        )}

        <h2 className={classes.includesTitle}>
          {t('you_will_learn')}
        </h2>
        <p className={classes.includesText}>
          {props.dataCourse.data.descripcion}
        </p>
      </div>
      <div
        className={
          props.accessCourse
            ? classes.productLearnInfoAccessSection
            : classes.productLearnInfoSection
        }
      >
        <h2 className={classes.includesTitle}>
          {t('this_course_include')}{' '}
          {/* Internationalized section title */}
        </h2>
        <div
          className={
            props.accessCourse
              ? classes.productIncludesItemsAccessContainer
              : classes.productIncludesItemsContainer
          }
        >
          {/* Lessons included */}
          <p
            className={
              props.accessCourse
                ? classes.productIncludesAccessItem
                : classes.productIncludesItem
            }
          >
            <MovieIcon className={classes.productIncludeIcon} />{' '}
            <span className={classes.infoIcon}>
              {props.lessons}{' '}
              {/* Number of lessons passed as prop */} {t('lessons')}{' '}
              <span className={classes.complementaryText}>
                ({props.hrs} {/* Total hours passed as prop */} hrs)
              </span>
            </span>
          </p>
          {/* Additional materials/files included */}
          <p
            className={
              props.accessCourse
                ? classes.productIncludesAccessItem
                : classes.productIncludesItem
            }
          >
            <DescriptionOutlinedIcon
              className={classes.productIncludeIcon}
            />{' '}
            <span className={classes.infoIcon}>
              {t('additional_files')} ({props.aditionalMaterial}{' '}
              {/* Number of additional materials passed as prop */})
            </span>
          </p>
          {/* Activities included */}
          <p
            className={
              props.accessCourse
                ? classes.productIncludesAccessItem
                : classes.productIncludesItem
            }
          >
            <AttachmentOutlinedIcon
              className={classes.productIncludeIcon}
            />{' '}
            <span className={classes.infoIcon}>
              {t('activities')}{' '}
              <span className={classes.complementaryText}>
                ({props.activities}{' '}
                {/* Number of activities passed as prop */})
              </span>
            </span>
          </p>
          {/* Course diploma included */}
          <p
            className={
              props.accessCourse
                ? classes.productIncludesAccessItemSpecial
                : classes.productIncludesItem
            }
          >
            <figure className={classes.iconIncludesContainer}>
              <img
                className={classes.img}
                src="https://d1g6c01s9ssd7h.cloudfront.net/img/consulta_diploma.png"
                alt="Diploma icon" // Accessibility: icon alt text
              />
            </figure>
            <span className={classes.infoIcon}>
              {t('course_diploma')}{' '}
              {/* Internationalized diploma text */}
            </span>
          </p>
        </div>
      </div>
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 * Provides runtime validation and serves as inline documentation
 */
CourseIncludes.propTypes = {
  /**
   * Complete course data including lessons, activities, and user progress
   * Passed as prop to calculate progress and render course details
   */
  dataCourse: PropTypes.object,
  /**
   * Number of lessons included in the course
   * Passed as prop to display lesson count
   */
  lessons: PropTypes.string,
  /**
   * Number of additional materials/files included
   * Passed as prop to display material count
   */
  aditionalMaterial: PropTypes.string,
  /**
   * Number of activities included in the course
   * Passed as prop to display activity count
   */
  activities: PropTypes.string,
  /**
   * Total hours of course content
   * Passed as prop to display total hours
   */
  hrs: PropTypes.string,
  /**
   * LTI integration data for grade passback and external platform communication
   * Passed as prop to render LTI messages and warnings
   */
  ltiData: PropTypes.object,
  /**
   * Boolean indicating whether the user has access to the course
   * Controls UI rendering and access to course features
   */
  accessCourse: PropTypes.bool
};

CourseIncludes.defaultProps = {
  dataCourse: {},
  lessons: '0',
  aditionalMaterial: '0',
  activities: '0',
  hrs: '0',
  ltiData: null,
  accessCourse: false
};

export default CourseIncludes;
